import { enumMeshTriangles, enumMeshLines, enumMeshVertices } from './VertexEnumerator';
import THREE from "three";
export const screenPickInfo = Symbol('screenPickInfo');

// All SVF2/OTG geometries are normalized to unit boxes. We can use this static box
// for a cheap geometry-space ray-box test to skip expensive per-triangle intersection.
var unitBox = new THREE.Box3(new THREE.Vector3(-0.5, -0.5, -0.5), new THREE.Vector3(0.5, 0.5, 0.5));

var inverseMatrix;
var ray;

var rayTarget;
var normal;

function init_three() {

  if (!inverseMatrix) {
    inverseMatrix = new THREE.Matrix4();
    ray = new THREE.Ray();

    rayTarget = new THREE.Vector3();
    normal = new THREE.Vector3();
  }
}

function meshRayCast(mesh, raycaster, intersects) {
  init_three();

  var geometry = mesh.geometry;

  if (!geometry)
  return;

  var material = mesh.material;

  var side = material ? material.side : THREE.FrontSide;

  inverseMatrix.copy(mesh.matrixWorld).invert();
  ray.copy(raycaster.ray).applyMatrix4(inverseMatrix);

  // Early-out: test ray against geometry-space bbox before expensive per-triangle test.
  // For SVF2/OTG geometries (identified by hash), all geometry is normalized to a unit box.
  // Therefore, we can use a static unitBox as geometry-space bbox — which is much better
  // aligned than the world-space AABB for long/diagonal shapes.
  var isOtg = !!geometry.hash;
  var localBox = geometry.boundingBox || (isOtg ? unitBox : null);
  if (localBox && !ray.intersectsBox(localBox)) {
    return;
  }

  var precision = raycaster.precision || 0.0001;
  var intersectionPoint, distance;


  enumMeshTriangles(geometry, function (vA, vB, vC, a, b, c, nA, nB, nC, idx) {

    if (side === THREE.BackSide) {

      intersectionPoint = ray.intersectTriangle(vC, vB, vA, true, rayTarget);

    } else {

      intersectionPoint = ray.intersectTriangle(vA, vB, vC, side !== THREE.DoubleSide, rayTarget);

    }

    if (intersectionPoint === null) return;

    intersectionPoint.applyMatrix4(mesh.matrixWorld);

    distance = raycaster.ray.origin.distanceTo(intersectionPoint);

    if (distance < precision || distance < raycaster.near || distance > raycaster.far) return;

    THREE.Triangle.getNormal(vA, vB, vC, normal);
    intersects.push({

      distance: distance,
      point: intersectionPoint.clone(),
      face: new THREE.Face3(a, b, c, normal.clone()),
      faceIndex: idx,
      fragId: mesh.fragId,
      dbId: mesh.dbId,
      object: mesh,
      modelId: mesh.modelId
    });
  });

}


function lineRayCast(mesh, raycaster, intersects, options) {

  init_three();

  var geometry = mesh.geometry;

  if (!geometry)
  return;

  // Use screen-space threshold when camera info is available.
  const pickInfo = options?.[screenPickInfo];
  let screenFactor = null;
  let isPerspective = null;
  if (pickInfo) {
    const lineHalfWidth = mesh.isWideLine ?
    (mesh.material.linewidth || mesh.geometry.lineWidth || 1) * 0.5 :
    0.5; // regular lines are 1px wide
    screenFactor = (lineHalfWidth + (pickInfo.hitMarginPixels || 0)) * pickInfo.unitsPerPixelFactor;

    isPerspective = pickInfo.isPerspective;
  } else {
    // Legacy fallback with fixed world-space threshold.
    screenFactor = raycaster.params.Line.threshold;
    // For wide lines, scale the threshold by the line width (in pixels).
    if (mesh.isWideLine) {
      if (mesh.material.linewidth) {
        screenFactor *= mesh.material.linewidth;
      } else if (mesh.geometry.lineWidth) {
        screenFactor *= mesh.geometry.lineWidth;
      }
    }
  }
  const screenFactorSq = screenFactor * screenFactor;

  var interSegment = new THREE.Vector3();
  var interRay = new THREE.Vector3();

  if (geometry instanceof THREE.BufferGeometry) {

    enumMeshLines(geometry, function (vStart, vEnd, a, b, idx) {

      var distance, distSq;

      // Transform segment to world space.
      vStart.applyMatrix4(mesh.matrixWorld);
      vEnd.applyMatrix4(mesh.matrixWorld);

      raycaster.ray.distanceSqToSegment(vStart, vEnd, interRay, interSegment);

      distSq = interSegment.distanceToSquared(interRay);

      if (isPerspective) {
        // For perspective, threshold grows with distance from camera
        const distAlongRaySq = raycaster.ray.origin.distanceToSquared(interRay);
        if (distSq > screenFactorSq * distAlongRaySq) return;
      } else {
        // Non-perspective or fixed world threshold (no pickInfo available)
        if (distSq > screenFactorSq) return;
      }

      distance = raycaster.ray.origin.distanceTo(interSegment);

      if (distance < raycaster.near || distance > raycaster.far) return;

      let result = {
        distance: distance,
        // What do we want? intersection point on the ray or on the segment??
        // point: raycaster.ray.at( distance ),
        point: interSegment.clone(), // Note that we might hit multiple segments within the same mesh, particularly because everything
        // within linePrecision radius is considered a hit. Therefore, we must clone the vector here.
        face: { a: a, b: b },
        faceIndex: idx,
        fragId: mesh.fragId,
        dbId: mesh.dbId,
        object: mesh,

        // Add distance to ray to allow estimating how far away this segment actually is from the ray.
        // For on-canvas hit-tests, this allows us to estimate the screens-space distance.
        distanceToRay: Math.sqrt(distSq)
      };

      /**
       * If there is extra filter function in the options, we should let it to do the filter inline here
       * Because: in FragmentList.prototype.getVizmesh
       *
       * The returned mesh object is a cached object (to reduce the gabage collection pressure, more important than code!!!), and it will not be the same mesh object later on when we run the filter function
       */
      if (options && options.filter && !options.filter(result)) return;

      intersects.push(result);
    });
  }
}

/// c.f. THREE.PointCloud.prototype.raycast()
function pointRayCast(mesh, raycaster, intersects, options) {

  const geometry = mesh.geometry;
  if (!geometry)
  return;

  const precision = raycaster.precision || 0.0001;

  if (geometry instanceof THREE.BufferGeometry) {

    const screenPickInfos = options?.[screenPickInfo];
    const pointHalfSize = (geometry.pointSize || 1) * 0.5;

    const isPerspective = screenPickInfos?.isPerspective;
    let screenFactor;
    if (screenPickInfos) {
      screenFactor = (pointHalfSize + (screenPickInfos.hitMarginPixels || 0)) * screenPickInfos.unitsPerPixelFactor;
    } else {
      screenFactor = (raycaster.params.PointCloud.threshold || 1) * Math.max(3, geometry.pointSize || 1) / 4;
    }
    const screenFactorSq = screenFactor * screenFactor;

    const m = mesh.matrixWorld.elements;

    // Build a composite matrix that transforms local coordinates directly
    // into a ray-aligned coordinate system where:
    //   Z = distance along the ray from the origin
    //   X, Y = orthogonal distances (perpDistSq = X² + Y²)
    // This folds matrixWorld + world-to-ray-basis into one 3x3 multiply
    // per vertex, replacing applyMatrix4 + closestPointToPoint + distanceTo.
    const D = raycaster.ray.direction;
    const O = raycaster.ray.origin;

    // Orthonormal basis: U and V perpendicular to D
    let ux, uy, uz, vx, vy, vz;
    if (Math.abs(D.x) < 0.9) {
      ux = 0;uy = -D.z;uz = D.y;
    } else {
      ux = D.z;uy = 0;uz = -D.x;
    }
    var uLen = Math.sqrt(ux * ux + uy * uy + uz * uz);
    ux /= uLen;uy /= uLen;uz /= uLen;
    vx = D.y * uz - D.z * uy;
    vy = D.z * ux - D.x * uz;
    vz = D.x * uy - D.y * ux;

    // Composite C = RayBasis * MatrixWorld_3x3 (column-major m[])
    const c00 = ux * m[0] + uy * m[1] + uz * m[2];
    const c01 = ux * m[4] + uy * m[5] + uz * m[6];
    const c02 = ux * m[8] + uy * m[9] + uz * m[10];
    const c10 = vx * m[0] + vy * m[1] + vz * m[2];
    const c11 = vx * m[4] + vy * m[5] + vz * m[6];
    const c12 = vx * m[8] + vy * m[9] + vz * m[10];
    const c20 = D.x * m[0] + D.y * m[1] + D.z * m[2];
    const c21 = D.x * m[4] + D.y * m[5] + D.z * m[6];
    const c22 = D.x * m[8] + D.y * m[9] + D.z * m[10];

    // Offset: RayBasis * (translation - rayOrigin)
    const tx = m[12] - O.x,ty = m[13] - O.y,tz = m[14] - O.z;
    const ox = ux * tx + uy * ty + uz * tz;
    const oy = vx * tx + vy * ty + vz * tz;
    const oz = D.x * tx + D.y * ty + D.z * tz;

    enumMeshVertices(geometry, function (point, normal, uv, idx) {
      const px = point.x,py = point.y,pz = point.z;
      const rx = c00 * px + c01 * py + c02 * pz + ox;
      const ry = c10 * px + c11 * py + c12 * pz + oy;
      const rz = c20 * px + c21 * py + c22 * pz + oz;

      if (rz < precision || rz < raycaster.near || rz > raycaster.far) {
        return;
      }

      var perpDistSq = rx * rx + ry * ry;
      if (isPerspective) {
        if (perpDistSq > screenFactorSq * rz * rz) {
          return;
        }
      } else {
        if (perpDistSq > screenFactorSq) {
          return;
        }
      }

      intersects.push({
        distance: rz, // This is the distance of the projection onto the ray, not distance to the point
        distanceToRay: Math.sqrt(perpDistSq),
        point: new THREE.Vector3(
          O.x + rz * D.x + rx * ux + ry * vx,
          O.y + rz * D.y + rx * uy + ry * vy,
          O.z + rz * D.z + rx * uz + ry * vz
        ),
        face: { a: idx },
        faceIndex: idx,
        fragId: mesh.fragId,
        dbId: mesh.dbId,
        object: mesh
      });
    });

  } else {

    // not implemented - other geometry types
  }
}

/**
 * Intersection Filtering Callback
 * @callback intersectionFilterCallback
 * @param {Intersection} element
 * @returns {boolean} True, if element should be kept.
 */

/**
 * Perform raycast on the mesh.
 * @param {THREE.Mesh} mesh
 * @param {THREE.RayCaster} raycaster
 * @param {Intersection[]} intersects - An object array that contains intersection result objects.
 * @param {Object} [options]
 * @param {intersectionFilterCallback} [options.filter] - Optional result filter callback. Only used for line meshes.
 * @param {boolean} [options.skipLines] - By default, lines are tested
 * @param {boolean} [options.skipPoints] - By default, point are tested
 * @param {boolean} [options.skipMeshes] - By default, meshes are tested
 */
function rayCast(mesh, raycaster, intersects, options) {

  if (mesh.isLine || mesh.isWideLine) {
    if (!options?.skipLines)
    lineRayCast(mesh, raycaster, intersects, options);
  } else
  if (mesh.isPoint) {
    if (!options?.skipPoints)
    pointRayCast(mesh, raycaster, intersects, options);
  } else
  if (!options?.skipMeshes)
  meshRayCast(mesh, raycaster, intersects);

}


function intersectObjectRec(object, raycaster, intersects, recursive) {

  if (object instanceof THREE.Mesh)
  rayCast(object, raycaster, intersects); //use our extended impl in case of Mesh.
  else
  object.raycast(raycaster, intersects); //fall back to normal THREE.js impl

  if (recursive === true) {

    var children = object.children;

    for (var i = 0, l = children.length; i < l; i++) {

      intersectObjectRec(children[i], raycaster, intersects, true);

    }

  }

}

var descSort = function (a, b) {
  return a.distance - b.distance;
};

function intersectObject(object, raycaster, intersects, recursive) {
  intersectObjectRec(object, raycaster, intersects, recursive);
  intersects.sort(descSort);
}


export let VBIntersector = {
  meshRayCast: meshRayCast,
  lineRayCast: lineRayCast,
  rayCast: rayCast,
  intersectObject: intersectObject
};